import React, { useContext, useEffect, useState } from "react";
import "./Dashboard.css";
import useTitle from "../Hooks/useTitle";
import ToastContext from "../Context/toastContext";
import axiosClient from "../Utils/axiosClient";
import { useTranslation } from "react-i18next";
import PieChart from "../Components/PieChart";
import LineChart from "../Components/LineChart";
import { createRoot } from "react-dom/client";
import GaugeChart from "../Components/GaugeChart";
import DashboardDatagrid from "../Components/DashboardDatagrid";
import ForbiddenPage from "./ForbiddenPage";
import Stepper from "../Components/Stepper";
import { useNavigate } from "react-router-dom";

const HomePage = () => {
  const [credit, setCredit] = useState([]);
  const [messageStat, setMessageStat] = useState([]);
  const [status, setStatus] = useState(null);
  const [remainPercentage, setRemainPercentage] = useState(null);
  const [usedPercentage, setUsedPercentage] = useState(null);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useContext(ToastContext);

  useTitle(`${t("Dashboard")} | Netileti`);

  //row-01 credits
  const fetchCredit = async () => {
    try {
      const response = await axiosClient.get("/dashboard/credits");

      if (response.status === 200) {
        let remainPercentage =
          (response.data.remain / response.data.total) * 100;
        let usedPercentage = 100 - remainPercentage;

        remainPercentage = parseFloat(remainPercentage.toFixed(1));
        usedPercentage = parseFloat(usedPercentage.toFixed(1));

        if (isNaN(remainPercentage)) remainPercentage = 0;
        if (isNaN(usedPercentage)) usedPercentage = 0;

        const formattedData = {
          total: new Intl.NumberFormat(navigator.language).format(
            response.data.total
          ),
          lastPurchase: new Intl.NumberFormat(navigator.language).format(
            response.data.lastPurchase
          ),
          used: new Intl.NumberFormat(navigator.language).format(
            response.data.used
          ),
          remain: new Intl.NumberFormat(navigator.language).format(
            response.data.remain
          ),
        };

        setRemainPercentage(remainPercentage);
        setUsedPercentage(usedPercentage);
        setCredit(formattedData);
      } else {
        showToast(
          `${t("Error")}: ${response.statusText}`,
          "Http status :",
          response.status
        );
        setStatus(response.status);
      }
    } catch (error) {
      showToast(
        `${t("Failed to fetch data. Please try again.")}`,
        `${t("Error")}`
      );
      setStatus(error.status);
    }
  };

  //row-02 messages
  const fetchMessageStatuses = async () => {
    try {
      const response = await axiosClient.get("/dashboard/summary");

      if (response.status === 200) {
        const formattedData = response.data;
        setMessageStat(formattedData);
      } else {
        showToast(
          `${t("Error")}: ${response.statusText}`,
          "Http status :",
          response.status
        );
      }
    } catch (error) {
      showToast(
        `${t("Failed to fetch data. Please try again.")}`,
        `${t("Error")}`
      );
      setStatus(error.status);
    }
  };

  useEffect(() => {
    fetchCredit();
    fetchMessageStatuses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //pie chart
    const pieChartContainer = document.getElementById("pie-chart-container");
    if (pieChartContainer) {
      pieChartContainer.innerHTML = "";
      const pieChartComponent = document.createElement("div");
      pieChartContainer.appendChild(pieChartComponent);
      const root = createRoot(pieChartComponent);
      root.render(<PieChart />);
    }

    //line chart
    const lineChartContainer = document.getElementById("line-chart-container");
    if (lineChartContainer) {
      lineChartContainer.innerHTML = "";
      const lineChartComponent = document.createElement("div");
      lineChartContainer.appendChild(lineChartComponent);
      const root = createRoot(lineChartComponent);
      root.render(<LineChart />);
    }

    //gauge chart
    const gaugeChartContainer = document.getElementById(
      "gauge-chart-container"
    );
    if (gaugeChartContainer) {
      gaugeChartContainer.innerHTML = "";
      const gaugeChartComponent = document.createElement("div");
      gaugeChartContainer.appendChild(gaugeChartComponent);
      const root = createRoot(gaugeChartComponent);
      root.render(<GaugeChart />);
    }
  }, [messageStat]);

  const goLogin = () => {
    navigate("/login");
    window.location.reload();
  };

  const messageStatStyle = { "--card-bg-color": "#27ade1" };

  let progressBarColor =
    usedPercentage > 85.0 ? "red" : usedPercentage >= 50.0 ? "orange" : "green";

  return (
    <div className="dashboard-content">
      {status === 401 ? (
        goLogin()
      ) : status === 403 ? (
        <ForbiddenPage />
      ) : (
        <div className="panels">
          <div className="stepper-container">
            <Stepper />
          </div>

          {/* Credits Section */}
          <div className="panel-template">
            <h3 className="row-title">{t("Credits")}</h3>
            <div className="row-01">
              <div className="card">
                <div className="title">{t("Total")}</div>
                <div className="card-content">
                  {credit?.total !== undefined ? credit.total : " "}
                </div>
              </div>
              <div className="card">
                <div className="title">{t("LastPurch")}</div>
                <div className="card-content">
                  {credit?.lastPurchase !== undefined
                    ? credit.lastPurchase
                    : " "}
                </div>
              </div>
              <div className="card">
                <div className="title">{t("Used")}</div>
                <div className="card-content">
                  {credit?.used !== undefined ? credit.used : " "}
                  <div className="progress-container">
                    <div className="progress-label">
                      <span> </span>
                      <span>{usedPercentage}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`progress-fill ${progressBarColor}`}
                        style={{ width: `${usedPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="title">{t("Balance")}</div>
                <div className="card-content">
                  {credit?.remain !== undefined ? credit.remain : " "}
                  <div className="progress-container">
                    <div className="progress-label">
                      <span> </span>
                      <span>{remainPercentage}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`progress-fill ${progressBarColor}`}
                        style={{ width: `${remainPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages Section */}
          <div className="panel-template">
            <h3 className="row-title">{t("Messages")}</h3>
            <div className="row-02">
              <div className="card-02" style={messageStatStyle}>
                <div className="title">{t("Today")}</div>
                <div className="card-content">
                  {messageStat?.daily !== undefined ? messageStat.daily : " "}
                </div>
              </div>
              <div className="card-02" style={messageStatStyle}>
                <div className="title">{t("Weekly")}</div>
                <div className="card-content">
                  {messageStat?.weekly !== undefined ? messageStat.weekly : " "}
                </div>
              </div>
              <div className="card-02" style={messageStatStyle}>
                <div className="title">{t("Monthly")}</div>
                <div className="card-content">
                  {messageStat?.monthly !== undefined
                    ? messageStat.monthly
                    : " "}
                </div>
              </div>
              <div className="card-02" style={messageStatStyle}>
                <div className="title">{t("Yearly")}</div>
                <div className="card-content">
                  {messageStat?.yearly !== undefined ? messageStat.yearly : " "}
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="panel-template">
            <h3 className="row-title">{t("Analytics")}</h3>
            <div className="charts-container">
              <div className="chart-card">
                <h4 className="chart-title">{t("Message Statistics")}</h4>
                <div id="pie-chart-container"></div>
              </div>
              <div className="chart-card">
                <h4 className="chart-title">{t("Monthly Messages")}</h4>
                <div id="line-chart-container"></div>
              </div>
              <div className="chart-card">
                <h4 className="chart-title">{t("Service Status")}</h4>
                <div id="gauge-chart-container"></div>
              </div>
            </div>
          </div>

          {/* Recent Messages Section */}
          <div className="panel-template">
            <h3 className="row-title">{t("Last Messages")}</h3>
            <div className="chart-card">
              <DashboardDatagrid />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
